import axios from "axios"
import { GettextInterface } from "@indeed/indeed-gettext"
import { toastError } from "../../common/components/indeed-toast-container"
import { logDatadogRum } from "../../common/utils/datadog"
import { asyncThunkOf } from "../../common/utils/redux-helpers"
import { ReqStatus } from "../../common/utils/utils"
import { AdvancedPauseDuration, IPauseStatusResponse, IPauseSubmitResponse, PausePageStatus, PauseSubmissionStatus } from "./pause-model"
import { initialSelectedDuration, pauseSlice } from "./pause-slice"

function pauseApiErrorToast(title: string, gettext: GettextInterface["gettext"]) {
    toastError(gettext("Refresh the page or try again later"), title)
}

export const submitPause = asyncThunkOf<
    ReqStatus,
    {
        encryptedInfo: string
        from?: string
        initialPause?: boolean
        selectedDuration?: AdvancedPauseDuration
        gettext: GettextInterface["gettext"]
    }
>("submitPause", async (payload, thunkApi) => {
    const { dispatch } = thunkApi
    const { encryptedInfo, initialPause, selectedDuration = initialSelectedDuration, gettext } = payload
    const apiUrl = thunkApi.getState().config.apiBase
    if (initialPause) {
        dispatch(pauseSlice.actions.updatePausePageStatus(PausePageStatus.SUBMITTING_INITIAL_PAUSE))
    }
    dispatch(pauseSlice.actions.updatePauseSubmissionStatus(PauseSubmissionStatus.SUBMITTING))
    const { pageStatus } = thunkApi.getState().pause
    const failedToSaveText = gettext("We couldn’t save your changes")

    return axios
        .post<IPauseSubmitResponse>(`${apiUrl}/pause`, selectedDuration, {
            params: {
                info: encryptedInfo,
            },
        })
        .then((response) => {
            if (response.data?.pauseData) {
                dispatch(
                    pauseSlice.actions.updatePauseStatus({
                        pauseData: response.data.pauseData,
                        isPaused: true,
                    })
                )
                dispatch(pauseSlice.actions.updatePausePageStatus(PausePageStatus.PAUSE_SUCCESS))
                return ReqStatus.SUCCESS
            }
            throw new Error(`Received unsuccessful response for submitting pause for info: ${encryptedInfo} duration: ${selectedDuration}`)
        })
        .catch((error) => {
            logDatadogRum(error)
            if (pageStatus === PausePageStatus.UNPAUSE_SUCCESS) {
                pauseApiErrorToast(failedToSaveText, gettext)
            } else if (pageStatus === PausePageStatus.SUBMITTING_INITIAL_PAUSE) {
                dispatch(pauseSlice.actions.updatePausePageStatus(PausePageStatus.INITIAL_PAUSE_ERROR))
            } else if (pageStatus === PausePageStatus.PAUSE_STATUS_LOADED) {
                dispatch(pauseSlice.actions.updatePausePageStatus(PausePageStatus.PAUSE_STATUS_LOADED_SUBMIT_ERROR))
                pauseApiErrorToast(failedToSaveText, gettext)
            } else {
                dispatch(pauseSlice.actions.updatePausePageStatus(PausePageStatus.OPTION_PAUSE_ERROR))
                pauseApiErrorToast(failedToSaveText, gettext)
            }
            return ReqStatus.FAILED
        })
        .finally(() => {
            dispatch(pauseSlice.actions.updatePauseSubmissionStatus(PauseSubmissionStatus.READY))
        })
})

export const submitUnpause = asyncThunkOf<ReqStatus, { encryptedInfo: string; gettext: GettextInterface["gettext"] }>(
    "submitUnpause",
    async (payload, thunkApi) => {
        const { dispatch } = thunkApi
        const { encryptedInfo, gettext } = payload
        const apiUrl = thunkApi.getState().config.apiBase

        dispatch(pauseSlice.actions.updatePauseSubmissionStatus(PauseSubmissionStatus.SUBMITTING))
        return axios
            .delete<boolean>(`${apiUrl}/pause`, {
                params: {
                    info: encryptedInfo,
                },
            })
            .then((response) => {
                if (response.data) {
                    dispatch(pauseSlice.actions.updatePausePageStatus(PausePageStatus.UNPAUSE_SUCCESS))
                    dispatch(pauseSlice.actions.resetPauseStatus())
                    return ReqStatus.SUCCESS
                }
                throw new Error(`Server failed to unpause jobseeker for info: ${encryptedInfo}`)
            })
            .catch((error) => {
                pauseApiErrorToast(gettext("We couldn’t save your changes"), gettext)
                logDatadogRum(error)
                return ReqStatus.FAILED
            })
            .finally(() => {
                dispatch(pauseSlice.actions.updatePauseSubmissionStatus(PauseSubmissionStatus.READY))
            })
    }
)

export const getPauseStatus = asyncThunkOf<void, { from?: string }>("getPauseStatus", async (payload, thunkApi) => {
    const { dispatch } = thunkApi
    const apiUrl = thunkApi.getState().config.apiBase
    return axios
        .get<IPauseStatusResponse>(`${apiUrl}/pause`, {})
        .then((response) => {
            dispatch(pauseSlice.actions.updatePauseStatus(response.data))
            dispatch(pauseSlice.actions.updatePausePageStatus(PausePageStatus.PAUSE_STATUS_LOADED))
        })
        .catch((error) => {
            dispatch(pauseSlice.actions.updatePausePageStatus(PausePageStatus.PAUSE_STATUS_ERROR))
            logDatadogRum(error)
        })
})
